import React, { memo, useMemo } from 'react';
import { Badge, Icon, Link, Menus, Text, useTheme } from '@Sage/hoi-poi-ui';

import { CAMPAIGNS_MARKETING } from 'constants/Entities';

import { getLiteral } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';

import { getMarketingCampaignStatusBadgeType } from '../../../utils/marketingCampaignStatus';

import './styles.scss';

function MarketingCampaignRow({ data }) {
    const theme = useTheme();
    const campaignId = data?.id || data?.Id || data?.ID;
    const recipients =
        Number(
            data?.recipientsCount ??
                data?.recipientCount ??
                data?.recipients ??
                data?.contactsCount,
        ) || 0;
    const opens =
        Number(data?.opensCount ?? data?.openCount ?? data?.readersCount ?? data?.readers) || 0;
    const clicks =
        Number(data?.clicksCount ?? data?.clickCount ?? data?.clickersCount ?? data?.clickers) || 0;
    const unsubscribed =
        Number(
            data?.unsubscribedCount ??
                data?.unsubscribeCount ??
                data?.canceledSubscriptionCount ??
                data?.cancelledSubscriptionCount,
        ) || 0;
    const showMetricRates = recipients > 0 && (opens > 0 || clicks > 0 || unsubscribed > 0);
    const subtitle =
        'Updated Mar 11, 2026, 9:41 AM by Alexandria Morgan on behalf of the Global Lifecycle Marketing Operations Team';
    const statusBadgeType = getMarketingCampaignStatusBadgeType(data?.statusId ?? data?.status);

    const menuItems = useMemo(() => {
        if (!campaignId) return [];

        return [
            {
                title: <Text>{getLiteral('label_marketing_campaigns_preview_and_test')}</Text>,
                icon: <Icon name="visibilityOn" />,
                onClick: () => ensureRoute(`${CAMPAIGNS_MARKETING.route}/edit/${campaignId}`),
            },
            {
                title: <Text>{getLiteral('label_detail')}</Text>,
                icon: <Icon name="infoOutlined" />,
                onClick: () => ensureRoute(`${CAMPAIGNS_MARKETING.route}/edit/${campaignId}`),
            },
            {
                title: <Text>{getLiteral('action_duplicate')}</Text>,
                icon: <Icon name="duplicate" />,
                onClick: () => {},
            },
        ];
    }, [campaignId]);

    const showActions = Boolean(campaignId);

    return (
        <div className="fm-marketing-campaign-row">
            <div className="fm-marketing-campaign-row__info">
                <div className="fm-marketing-campaign-row__title-wrap">
                    <Link href={`#${CAMPAIGNS_MARKETING.route}/edit/${campaignId}`}>
                        <Text
                            className="fm-marketing-campaign-row__title"
                            type="subtitle"
                            medium
                            isTruncated
                            useTooltip
                        >
                            {data?.title ||
                                data?.name ||
                                data?.subject ||
                                'Q4 enterprise reactivation campaign for inactive accounts across North America and Western Europe'}
                        </Text>
                    </Link>
                </div>
                {subtitle && (
                    <Text
                        className="fm-marketing-campaign-row__subtitle"
                        color={theme.colors.utility.textSecondary}
                        type="caption"
                        isTruncated
                        useTooltip
                    >
                        {subtitle}
                    </Text>
                )}
            </div>
            <div className="fm-marketing-campaign-row__status-column">
                {(data?.statusDescription || data?.status?.label || data?.status?.name) && (
                    <Badge
                        className="fm-marketing-campaign-row__status"
                        isTruncated
                        useTooltip
                        type={statusBadgeType}
                    >
                        {data?.statusDescription ||
                            data?.status?.label ||
                            data?.status?.name ||
                            'Scheduled for final legal approval and segmented rollout'}
                    </Badge>
                )}
            </div>
            <div className="fm-marketing-campaign-row__metrics">
                <div className="fm-marketing-campaign-row__metric">
                    <Text
                        className="fm-marketing-campaign-row__metric-label"
                        color={theme.colors.utility.textSecondary}
                        type="caption"
                    >
                        {getLiteral('label_recipients')}
                    </Text>
                    <Text
                        className="fm-marketing-campaign-row__metric-value"
                        type="subtitle"
                        medium
                    >
                        {recipients || '999,999'}
                    </Text>
                    <Text
                        className="fm-marketing-campaign-row__metric-rate"
                        color={theme.colors.utility.textSecondary}
                        type="badges"
                    >
                        {showMetricRates ? '100%' : '100.00%'}
                    </Text>
                </div>
                <div className="fm-marketing-campaign-row__metric">
                    <Text
                        className="fm-marketing-campaign-row__metric-label"
                        color={theme.colors.utility.textSecondary}
                        type="caption"
                    >
                        {getLiteral('label_marketing_campaigns_metrics_opens')}
                    </Text>
                    <Text
                        className="fm-marketing-campaign-row__metric-value"
                        type="subtitle"
                        medium
                    >
                        {opens || '123,456'}
                    </Text>
                    <Text
                        className="fm-marketing-campaign-row__metric-rate"
                        color={theme.colors.utility.textSecondary}
                        type="badges"
                    >
                        {showMetricRates && opens
                            ? `${((opens / recipients) * 100).toFixed(2)}%`
                            : '12.35%'}
                    </Text>
                </div>
                <div className="fm-marketing-campaign-row__metric">
                    <Text
                        className="fm-marketing-campaign-row__metric-label"
                        color={theme.colors.utility.textSecondary}
                        type="caption"
                    >
                        {getLiteral('label_marketing_campaigns_metrics_clicks')}
                    </Text>
                    <Text
                        className="fm-marketing-campaign-row__metric-value"
                        type="subtitle"
                        medium
                    >
                        {clicks || '12,345'}
                    </Text>
                    <Text
                        className="fm-marketing-campaign-row__metric-rate"
                        color={theme.colors.utility.textSecondary}
                        type="badges"
                    >
                        {showMetricRates && clicks
                            ? `${((clicks / recipients) * 100).toFixed(2)}%`
                            : '1.23%'}
                    </Text>
                </div>
                <div className="fm-marketing-campaign-row__metric fm-marketing-campaign-row__metric--wide">
                    <Text
                        className="fm-marketing-campaign-row__metric-label"
                        color={theme.colors.utility.textSecondary}
                        type="caption"
                    >
                        {getLiteral('label_marketing_campaigns_metrics_unsubscribed')}
                    </Text>
                    <Text
                        className="fm-marketing-campaign-row__metric-value"
                        type="subtitle"
                        medium
                    >
                        {unsubscribed || '1,234'}
                    </Text>
                    <Text
                        className="fm-marketing-campaign-row__metric-rate"
                        color={theme.colors.utility.textSecondary}
                        type="badges"
                    >
                        {showMetricRates && unsubscribed
                            ? `${((unsubscribed / recipients) * 100).toFixed(2)}%`
                            : '0.12%'}
                    </Text>
                </div>
            </div>
            {showActions && (
                <div className="fm-marketing-campaign-row__actions">
                    <Icon
                        name="edit"
                        size="large"
                        color={theme.colors.actionMinor[500]}
                        onClick={() =>
                            ensureRoute(`${CAMPAIGNS_MARKETING.route}/edit/${campaignId}`)
                        }
                    />
                    <Menus
                        icon={<Icon name="threeDots" color={theme.colors.actionMinor[500]} />}
                        items={menuItems}
                    />
                </div>
            )}
        </div>
    );
}

export default memo(MarketingCampaignRow);
